import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { AiOutlineLogout, AiOutlineDownload, AiOutlineDelete } from "react-icons/ai"; // Icons for buttons

const Dashboard = () => {
  const [data, setData] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          setError("You must be logged in to view this page.");
          return;
        }

        const response = await fetch(`${process.env.REACT_APP_API_URL}work/dashboard`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          setError("Failed to fetch data.");
          return;
        }

        const result = await response.json();
        setData(result);
      } catch (err) {
        setError("An error occurred while fetching data.");
      }
    };

    fetchData();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    window.location.href = "/login";
  };

  const handleDeleteFile = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setError("You must be logged in to delete the file.");
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}work/delete-file`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (response.ok) {
        alert(result.message);
        setData([]); // Optionally clear the displayed data after deletion
      } else {
        alert(result.error);
      }
    } catch (err) {
      setError("An error occurred while deleting the file.");
    }
  };

  if (error) {
    return (
      <div className="mt-6 text-center">
        <p className="font-semibold text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 bg-gray-50">
      {/* Header Section */}
      <div className="flex items-center justify-between pb-4 mb-6 border-b">
        <h2 className="text-3xl font-semibold text-gray-800">Work Completion Dashboard</h2>
        <button
          onClick={handleLogout}
          className="flex items-center px-4 py-2 space-x-2 text-white transition bg-red-600 rounded-full hover:bg-red-700"
        >
          <AiOutlineLogout size={20} />
          <span>Logout</span>
        </button>
      </div>

      {/* Table Section */}
      <div className="mb-6 overflow-x-auto bg-white rounded-lg shadow-lg">
        <table className="min-w-full text-left table-auto">
          <thead className="text-gray-700 bg-gray-100">
            <tr>
              {[
                "Work Date", "Type of Work", "Work Description", "Building", "Level", 
                "Rack Number", "Remarks", "Work Order Number", "Work Completion Percentage", "QTY", "Work Supervisor"
              ].map((header, index) => (
                <th key={index} className="p-3 text-sm font-medium border-b">{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((entry, index) => (
              <tr key={index} className="border-b hover:bg-gray-50">
                <td className="p-3">{entry["Work Date"]}</td>
                <td className="p-3">{entry["Type of Work"]}</td>
                <td className="p-3">{entry["Work Description"]}</td>
                <td className="p-3">{entry["Building"]}</td>
                <td className="p-3">{entry["Level"]}</td>
                <td className="p-3">{entry["Rack Number"]}</td>
                <td className="p-3">{entry["Remarks"]}</td>
                <td className="p-3">{entry["Work Order Number"]}</td>
                <td className="p-3">{entry["Work Completion Percentage"]}</td>
                <td className="p-3">{entry["Qty"]}</td>
                <td className="p-3">{entry["Work Supervisor"]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end space-x-4">
        <DownloadExcelButton data={data} />
        <button
          onClick={handleDeleteFile}
          className="flex items-center px-6 py-3 space-x-2 text-white transition bg-red-600 rounded-full hover:bg-red-700"
        >
          <AiOutlineDelete size={20} />
          <span>Delete Excel File</span>
        </button>
      </div>
    </div>
  );
};

const DownloadExcelButton = ({ data }) => {
  const handleDownload = () => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Work Data");

    XLSX.writeFile(workbook, "work_data.xlsx");
  };

  return (
    <button
      onClick={handleDownload}
      className="flex items-center px-6 py-3 space-x-2 text-white transition bg-blue-600 rounded-full hover:bg-blue-700"
    >
      <AiOutlineDownload size={20} />
      <span>Download Excel</span>
    </button>
  );
};

export default Dashboard;
